import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  History, Camera, RotateCcw, Trash2, Plus, AlertTriangle, Check, ChevronRight, FileDiff,
} from 'lucide-react';
import {
  instances as instancesApi,
  type DatenStandPlan, type DatenStand, type PfadVorschlag,
} from '../api';

/**
 * Die Zeitmaschine für alles, was eine Instanz zur Laufzeit schreibt.
 *
 * ══ DER FALL DAHINTER ══════════════════════════════════════════════════════
 *
 * Am 29.09.2026 verschwanden die Social-Media-Beiträge der open.junixx-Seite.
 * Die Verwaltungsmaske schickt beim Speichern die ganze Liste und ersetzt damit
 * die Datei; wer sie lange offen hatte, schrieb seinen älteren Stand über den
 * neueren. Es gab keinen Weg zurück — die Datenbanksicherung greift dort nicht,
 * denn die Seite hat gar keine Datenbank. Ihre Daten *sind* Dateien.
 *
 * ══ WARUM DIE PFADE AUSGEWÄHLT WERDEN ══════════════════════════════════════
 *
 * Nachgemessen über alle Instanzen: neben den kleinen Ablagen (61 kB, fünf
 * Dateien) hängen an denselben Instanzen Datenbankverzeichnisse mit 65–69 MB
 * und rund 1500 Dateien. Die zu versionieren wäre nicht nur teuer, sondern
 * falsch — eine Kopie laufender Datenbankdateien ist im besten Fall unbrauchbar
 * und im schlimmsten scheinbar brauchbar. Der PC schlägt deshalb vor und
 * begründet, was er ablehnt; ausgewählt wird von Hand.
 */

const TAKTE: { wert: number | null; label: string }[] = [
  { wert: null, label: 'nur beim Ausrollen' },
  { wert: 60, label: 'stündlich' },
  { wert: 360, label: 'alle 6 Stunden' },
  { wert: 1440, label: 'täglich' },
  { wert: 10080, label: 'wöchentlich' },
];

const ANLASS: Record<string, string> = {
  plan: 'Zeitplan',
  deploy: 'vor dem Ausrollen',
  hand: 'von Hand',
};

function groesse(bytes: number | string): string {
  const n = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} kB`;
  return `${n} B`;
}

/** Die Stände eines Plans, mit Vergleich und Rücksprung. */
function Staende({ instanceId, plan }: { instanceId: string; plan: DatenStandPlan }) {
  const qc = useQueryClient();
  const [diff, setDiff] = useState<{ sha: string; text: string } | null>(null);
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);

  const liste = useQuery({
    queryKey: ['datenstaende', instanceId, plan.id],
    queryFn: () => instancesApi.datenStandListe(instanceId, plan.id),
  });

  const vergleichen = useMutation({
    mutationFn: (sha: string) => instancesApi.datenStandVergleich(instanceId, plan.id, sha),
    onSuccess: (r, sha) => setDiff({ sha, text: r.text }),
  });

  const zurueck = useMutation({
    mutationFn: (sha: string) => instancesApi.datenStandZurueckholen(instanceId, plan.id, sha),
    onSuccess: (r) => {
      setMeldung({ ok: r.ok, text: r.meldung });
      setDiff(null);
      qc.invalidateQueries({ queryKey: ['datenstaende', instanceId] });
    },
    onError: (e: Error) => setMeldung({ ok: false, text: e.message }),
  });

  const staende = liste.data?.staende ?? [];

  return (
    <div className="border-t border-slate-100 bg-slate-50/60">
      {meldung && (
        <p className={`px-4 py-2 text-[11px] leading-relaxed ${
          meldung.ok ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'
        }`}>
          {meldung.text}
        </p>
      )}

      {liste.isLoading ? (
        <p className="px-4 py-2 text-[11px] text-slate-400">wird geladen …</p>
      ) : staende.length === 0 ? (
        <p className="px-4 py-2 text-[11px] text-slate-500">Noch kein Stand festgehalten.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {staende.map((s: DatenStand, i: number) => (
            <li key={s.id} className="px-4 py-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-[10px] text-slate-500">{s.sha.slice(0, 7)}</code>
                <span className="text-[11px] text-slate-800">
                  {new Date(s.createdAt).toLocaleString('de-DE')}
                </span>
                <span className="rounded bg-slate-200 px-1.5 text-[10px] text-slate-600">
                  {ANLASS[s.anlass] ?? s.anlass}
                </span>
                {i === 0 && (
                  <span className="rounded bg-emerald-100 px-1.5 text-[10px] text-emerald-800">
                    aktuell
                  </span>
                )}
                <span className="text-[10px] text-slate-400">
                  {s.dateien} Dateien, {groesse(s.bytes)}
                  {s.aenderung ? ` · ${s.aenderung}` : ''}
                  {s.ausgeloestVon ? ` · ${s.ausgeloestVon}` : ''}
                </span>

                <span className="ml-auto flex gap-1">
                  <button
                    type="button"
                    title="Was hat sich seitdem geändert?"
                    onClick={() => vergleichen.mutate(s.sha)}
                    disabled={vergleichen.isPending}
                    className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-white disabled:opacity-40"
                  >
                    <FileDiff size={10} />
                  </button>
                  {i !== 0 && (
                    <button
                      type="button"
                      title="Diesen Stand zurückholen"
                      onClick={() => {
                        if (!confirm(
                          `Stand ${s.sha.slice(0, 7)} vom ${new Date(s.createdAt).toLocaleString('de-DE')} zurückholen?\n\n`
                          + 'Der aktuelle Stand wird vorher festgehalten — der Weg zurück bleibt offen.\n'
                          + 'Dateien, die es damals nicht gab, werden entfernt.',
                        )) return;
                        zurueck.mutate(s.sha);
                      }}
                      disabled={zurueck.isPending}
                      className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900 hover:bg-amber-100 disabled:opacity-40"
                    >
                      <RotateCcw size={10} />
                    </button>
                  )}
                </span>
              </div>

              {diff?.sha === s.sha && (
                <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-[10px] leading-relaxed text-slate-200">
                  {diff.text}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DatenStaendePanel({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [offen, setOffen] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const plaene = useQuery({
    queryKey: ['datenstaende', instanceId],
    queryFn: () => instancesApi.datenStaende(instanceId),
  });

  const vorschlaege = useQuery({
    queryKey: ['datenstand-vorschlaege', instanceId],
    queryFn: () => instancesApi.datenStandVorschlaege(instanceId),
    enabled: neuOffen,
  });

  const anlegen = useMutation({
    mutationFn: (pfad: string) =>
      instancesApi.datenStandPlanAnlegen(instanceId, { pfad, beiDeploy: true, maxStaende: 50 }),
    onSuccess: () => {
      setNeuOffen(false);
      setFehler(null);
      qc.invalidateQueries({ queryKey: ['datenstaende', instanceId] });
    },
    onError: (e: unknown) => setFehler(
      (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      ?? (e as Error).message,
    ),
  });

  const entfernen = useMutation({
    mutationFn: (planId: string) => instancesApi.datenStandPlanEntfernen(instanceId, planId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['datenstaende', instanceId] }),
  });

  const jetzt = useMutation({
    mutationFn: (planId: string) => instancesApi.datenStandFesthalten(instanceId, planId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['datenstaende', instanceId] }),
  });

  const liste = plaene.data?.plaene ?? [];

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <History size={14} /> Datenstände
            </h3>
            <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
              Versionierte Stände dessen, was die Instanz zur Laufzeit schreibt — und was nicht
              im Image steht. Vor jedem Ausrollen wird einer festgehalten.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setNeuOffen((v) => !v); setFehler(null); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            <Plus size={12} /> Verzeichnis aufnehmen
          </button>
        </div>

        {neuOffen && (
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
            {vorschlaege.isLoading ? (
              <p className="text-[11px] text-slate-400">
                Der Wirt wird nach den Verzeichnissen gefragt und sie werden gemessen …
              </p>
            ) : vorschlaege.isError ? (
              <p className="text-[11px] text-rose-700">{(vorschlaege.error as Error).message}</p>
            ) : (
              <ul className="space-y-1">
                {(vorschlaege.data?.vorschlaege ?? []).map((v: PfadVorschlag) => (
                  <li key={v.pfad} className="flex flex-wrap items-start gap-2">
                    <code className="font-mono text-[11px] text-slate-800">{v.pfad}</code>
                    <span className="text-[10px] text-slate-400">
                      {groesse(v.bytes)}, {v.dateien} Dateien
                    </span>
                    {v.hindernis ? (
                      <span className="flex-1 text-[10px] leading-relaxed text-amber-800">
                        {v.hindernis}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => anlegen.mutate(v.pfad)}
                        disabled={anlegen.isPending || liste.some((p) => p.pfad === v.pfad)}
                        className="rounded bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-40"
                      >
                        {liste.some((p) => p.pfad === v.pfad) ? 'schon dabei' : 'aufnehmen'}
                      </button>
                    )}
                  </li>
                ))}
                {!vorschlaege.data?.vorschlaege.length && (
                  <li className="text-[11px] text-slate-500">
                    Diese Instanz schreibt nichts in ein eingebundenes Verzeichnis.
                  </li>
                )}
              </ul>
            )}
            {fehler && <p className="mt-1.5 text-[11px] text-rose-700">{fehler}</p>}
          </div>
        )}

        {plaene.isLoading ? (
          <p className="px-4 py-3 text-[11px] text-slate-400">wird geladen …</p>
        ) : liste.length === 0 ? (
          <p className="px-4 py-3 text-[11px] leading-relaxed text-slate-500">
            Noch kein Verzeichnis aufgenommen. Ohne einen Stand gibt es keinen Weg zurück, wenn
            eine Maske einmal den falschen Stand speichert.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {liste.map((p: DatenStandPlan) => (
              <div key={p.id}>
                <div className="flex flex-wrap items-center gap-2 px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setOffen(offen === p.id ? null : p.id)}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                  >
                    <ChevronRight
                      size={12}
                      className={`shrink-0 text-slate-400 transition-transform ${offen === p.id ? 'rotate-90' : ''}`}
                    />
                    <code className="truncate font-mono text-xs text-slate-800">{p.pfad}</code>
                    <span className="shrink-0 text-[10px] text-slate-400">
                      {p.anzahl} {p.anzahl === 1 ? 'Stand' : 'Stände'}
                      {' · '}
                      {TAKTE.find((t) => t.wert === p.intervallMinuten)?.label
                        ?? `alle ${p.intervallMinuten} Min`}
                      {p.beiDeploy && ' + beim Ausrollen'}
                    </span>
                  </button>

                  <button
                    type="button"
                    title="Jetzt einen Stand festhalten"
                    onClick={() => jetzt.mutate(p.id)}
                    disabled={jetzt.isPending}
                    className="rounded border border-slate-300 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                  >
                    <Camera size={11} />
                  </button>
                  <button
                    type="button"
                    title="Verzeichnis nicht mehr versionieren"
                    onClick={() => {
                      if (confirm(`${p.pfad} nicht mehr versionieren?\n\nDie bisherigen Stände bleiben auf dem Wirt erhalten.`))
                        entfernen.mutate(p.id);
                    }}
                    className="rounded border border-slate-300 px-2 py-1 text-[10px] text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>

                {p.letzterFehler && (
                  <p className="flex items-start gap-1.5 bg-rose-50 px-4 py-1.5 text-[10px] leading-relaxed text-rose-900">
                    <AlertTriangle size={11} className="mt-px shrink-0" />
                    Letzter Lauf gescheitert: {p.letzterFehler}
                  </p>
                )}

                {offen === p.id && <Staende instanceId={instanceId} plan={p} />}
              </div>
            ))}
          </div>
        )}

        {jetzt.data && (
          <p className="flex items-start gap-1.5 border-t border-slate-100 px-4 py-1.5 text-[11px] text-slate-600">
            <Check size={11} className="mt-0.5 shrink-0 text-emerald-600" />
            {jetzt.data.meldung}
          </p>
        )}
      </div>
    </div>
  );
}
